/**
 * Dynamic Title and Favicon Manager based on domain
 * This utility manages page titles and favicons dynamically based on the current domain
 */

const GLOBAL_APEX_ADMIN = {
  title: 'Global Apex Markets Admin',
  favicon: '/favicons/globalapex-favicon.png'
};

// Domain configuration mapping
const DOMAIN_CONFIG = {
  'admin.globalapexmarkets.com': GLOBAL_APEX_ADMIN,
  'appadmin.globalapexmarkets.com': GLOBAL_APEX_ADMIN,
  'admin.fxcapital24.com': {
    title: 'FX Capital Admin',
    favicon: '/fxcapital.ico' // FX Capital specific favicon
  },
  'admin.moneyplantfxfze.com': {
    title: 'Moneyplant Admin',
    favicon: '/favicon.ico' // Default favicon
  },
  // This branch is the Global Apex admin, so unknown hosts (localhost, preview URLs) get its brand too
  'default': GLOBAL_APEX_ADMIN
};

/**
 * Get domain-specific configuration
 * @returns {Object} Configuration object with title and favicon
 */
export const getDomainConfig = () => {
  const hostname = window.location.hostname;
  return DOMAIN_CONFIG[hostname] || DOMAIN_CONFIG['default'];
};

/**
 * Set page title dynamically based on domain
 * @param {string} pageTitle - The specific page title (e.g., "Dashboard", "Users List")
 */
export const setDynamicTitle = (pageTitle) => {
  const domainConfig = getDomainConfig();
  const fullTitle = pageTitle ? `${pageTitle} | ${domainConfig.title}` : domainConfig.title;
  
  // Update title without causing flicker
  if (document.title !== fullTitle) {
    document.title = fullTitle;
  }
};

/**
 * Set favicon dynamically based on domain
 * For this implementation, we'll use the same favicon but this can be extended
 */
export const setDynamicFavicon = () => {
  const domainConfig = getDomainConfig();
  const favicon = domainConfig.favicon;
  
  // Check if favicon element already exists
  let faviconElement = document.querySelector('link[rel="icon"]');
  if (!faviconElement) {
    faviconElement = document.createElement('link');
    faviconElement.rel = 'icon';
    document.head.appendChild(faviconElement);
  }
  
  // Update favicon href if different
  if (faviconElement.href !== window.location.origin + favicon) {
    faviconElement.href = favicon;
  }
};

/**
 * Initialize dynamic title and favicon management
 * This should be called once when the app loads
 */
export const initializeDynamicTitleManager = () => {
  // Set initial favicon
  setDynamicFavicon();
  
  // Create a MutationObserver to watch for title changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.target === document.head) {
        // Check if title was added/modified
        const titleElement = document.querySelector('title');
        if (titleElement) {
          // If title was just added and has content, update it with domain prefix
          if (titleElement.textContent && !titleElement.textContent.includes(' | ')) {
            // Extract the page title part (before any domain prefix)
            const pageTitle = titleElement.textContent.replace(/^(.+?)\s*\|\s*.+$/, '$1');
            setDynamicTitle(pageTitle);
          }
        }
      } else if (mutation.type === 'characterData' || mutation.type === 'attributes') {
        // Handle direct title text changes
        if (mutation.target.parentNode && mutation.target.parentNode.tagName === 'TITLE') {
          const currentTitle = document.title;
          if (currentTitle && !currentTitle.includes(' | ')) {
            // Extract the page title part (before any domain prefix)
            const pageTitle = currentTitle.replace(/^(.+?)\s*\|\s*.+$/, '$1');
            setDynamicTitle(pageTitle);
          }
        }
      }
    });
  });
  
  // Start observing changes to the head element
  observer.observe(document.head, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true
  });
  
  return observer;
};